import styled, { css } from 'styled-components';
import React from 'react';
import PropTypes from 'prop-types';
import { rem } from 'polished';

import LinkWrapper from './LinkWrapper';
import { fontSizes, colors } from '../helpers/variables';
import { displayable, gapable, layerable } from '../helpers/traits';

/**
 * Erzeugt einen Button
 * @param {string} props.children Der Inhalt des Buttons (zumeist Text)
 * @param {string} props.className Optional: Klasse für erweitertest Styling
 * @param {string} props.to: Optional: Link des Buttons (kann intern, extern, Anker sein)
 * @param {string} props.onClick: Optional: onClickHandler
 * @param {string} props.display Optional: Die Darstellung (block/inline/...)
 * @param {string} props.gap Optional: Der Abstand nach unten ('s', 'm', 'l', 'xl', 'xxl', 'xxxl')
 * @param {string} props.ghost Optional: Die Darstellung (Ghost-Button / normaler Button)
 * @param {boolean} props.noHover Optional: Hover-Effekt deaktivieren
 * @param {number} props.layer Optional: Die Ebene/Größe des Schattens
 * @param {bool} props.rounded Optional: Ecken abgerundet oder gerade
 * @param {string} props.size: Optional: Die Größe des Buttons ('s', 'm')
 *
 *
 * @example <Button display="block" gap="l" ghost layer={2} rounded size="s">Test</Button>
 */
const Button = styled(({ className, children, to, onClick, type, disabled }) => {
    let Element = (
        // eslint-disable-next-line react/button-has-type
        <button className={className} type={type} onClick={onClick} disabled={disabled}>
            {children}
        </button>
    );

    if (to && !onClick) {
        Element = (
            <LinkWrapper to={to} className={className}>
                {children}
            </LinkWrapper>
        );
    }

    return Element;
})`
    background-color: ${colors.primary};
    border: ${rem(2)} solid transparent;
    color: ${colors.white};
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    ${({ size }) => fontSizes[size] || fontSizes.m};
    padding: 0.35em 1em 0.35em;
    transition: background-color 150ms, color 150ms;
    line-height: 1.5;
    text-decoration: none;
    text-align: center;
    opacity: ${({ disabled }) => (disabled ? '0.7' : '1')};
    ${displayable('inline-block')};
    ${gapable()};

    ${({ noHover }) =>
        noHover
            ? null
            : css`
                  &:hover {
                      background-color: ${colors.primaryDark};
                      color: ${colors.white};
                      text-decoration: none;
                  }
              `};

    ${({ ghost }) =>
        ghost
            ? css`
                  border-color: ${colors.primary};
                  background-color: transparent;
                  color: ${colors.primary};

                  &:hover {
                      border-color: transparent;
                      background-color: ${colors.primaryDark};
                      color: ${colors.white};
                  }
              `
            : null};

    ${({ whiteGhost }) =>
        whiteGhost
            ? css`
                  border-color: ${colors.white};
                  background-color: transparent;
                  color: ${colors.white};

                  &:hover {
                      border-color: transparent;
                      background-color: ${colors.primaryDark};
                      color: ${colors.white};
                  }
              `
            : null};
`;

Button.propTypes = {
    className: PropTypes.string,
    children: PropTypes.node.isRequired,
    display: displayable.propType,
    gap: gapable.propType,
    ghost: PropTypes.bool,
    whiteGhost: PropTypes.bool,
    layer: layerable.propType,
    to: PropTypes.string,
    noHover: PropTypes.bool,
    onClick: PropTypes.func,
    size: PropTypes.oneOf(['xs', 's', 'm']),
    type: PropTypes.oneOf(['button', 'submit']),
    disabled: PropTypes.bool,
};

Button.defaultProps = {
    className: null,
    display: null,
    gap: null,
    ghost: false,
    whiteGhost: false,
    layer: null,
    to: null,
    noHover: false,
    onClick: null,
    size: 's',
    type: 'button',
    disabled: false,
};

export default Button;
